import { getChartDataOptionsEditingSchemas } from './chart-options-schemas';
import {
	BuildChartContext,
	ExtendedChartWidgetProps,
	updateChartSchema,
} from '../types';
import {
	zodToReadableType,
	filterSchemaProperties,
	excludeSchemaProperties,
} from '@/utils/zod-utils';
import { ChartWidgetProps } from '@sisense/sdk-ui';
import { createStructuredCompletion } from '@/utils/chat-completion-utils';
import { devDebug } from '@/utils/dev-debug';
import { z } from 'zod';

type UpdateChartDataOptionsHelperParams = {
	systemPrompt: string;
	userPrompt: string;
	chartWidgetProps: ExtendedChartWidgetProps;
};

/**
 * Builds a system prompt for chart data options editing.
 * @param chartWidgetProps - The chart widget props containing current data options
 * @param filteredSchema - The filtered schema for the specific subtool
 * @param additionalInstructions - Optional additional instructions to append to the prompt
 * @returns The complete system prompt string
 */
export const buildSystemPromptForDataOptions = (
	chartWidgetProps: ExtendedChartWidgetProps,
	filteredSchema: z.ZodTypeAny,
	additionalInstructions?: string,
): string => {
	const { dataOptions: currentDataOptions } = chartWidgetProps;

	// Ensure we have a ZodObject to access shape property
	if (filteredSchema?._def?.typeName !== 'ZodObject') {
		throw new Error(
			'Expected filteredSchema to be a ZodObject, but got a different type',
		);
	}
	const schema = filteredSchema as z.ZodObject<any>;

	const editableFieldsDoc = `
		Editable chart data options properties are as follows:

		dataOptions: {
			${Object.entries(schema.shape)
				.map(
					([key, value]) =>
						`- ${key}: ${zodToReadableType(value as z.ZodTypeAny)}`,
				)
				.join('\n')}
		}

		current dataOptions: {
			${JSON.stringify(currentDataOptions, null, 2)}
		}
		`;

	const systemPrompt = `
			You are a chart data options assistant. You edit only the fields the user mentions.

			DO NOT use any tools.
			
			Here are the editable properties:
			${editableFieldsDoc}
			
			Your output must be a JSON object with only the requested changes.

			The 'column' object, containing only the item's 'name', MUST be included for any change to an item within a list field ('value', 'category', 'breakBy', etc.) to maintain identity.
            
            Do not include unchanged fields.
            ${
				additionalInstructions
					? `\n            ${additionalInstructions}`
					: ''
			}
            
            Example:
            
            User: "Sort brands by total revenue in descending order"
            Output: { "dataOptions": { "value" : [
                {
                    "column": {
                        "name": "Total Revenue",
                    },
                    "sortType": "sortDesc"
                }
            ] } }

			`;

	return systemPrompt;
};

/**
 * Updates chart data options based on the user's prompt.
 * @param systemPrompt - The system prompt to use for the LLM completion.
 * @param userPrompt - The user's prompt to update the chart data options.
 * @param chartWidgetProps - The chart widget props to update the chart data options for.
 * @returns The updated chart.
 */
export const updateChartDataOptionsHelper = async ({
	systemPrompt,
	userPrompt,
	chartWidgetProps,
}: UpdateChartDataOptionsHelperParams): Promise<ExtendedChartWidgetProps> => {
	const { chartType: currentChartType } = chartWidgetProps;

	// Get the schema for the current chart type
	const baseSchema = getChartDataOptionsEditingSchemas(currentChartType);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used for z.infer type only
	const targetSchema = z.object({
		dataOptions: baseSchema,
	});

	const parsedResponse = await createStructuredCompletion({
		systemPrompt,
		userPrompt,
		responseFormat: { type: 'json_object' },
	});

	devDebug('toolCall', {
		tool: 'updateChartDataOptionsHelper',
		phase: 'dataOptionsDelta',
		parsedResponse,
	});

	const { dataOptions: responseDataOptions = null } =
		parsedResponse as z.infer<typeof targetSchema>;

	const mergedDataOptions = responseDataOptions
		? mergeDataOptions(chartWidgetProps.dataOptions, responseDataOptions)
		: chartWidgetProps.dataOptions;

	const updatedWidgetProps: ExtendedChartWidgetProps = {
		...chartWidgetProps,
		dataOptions: mergedDataOptions,
	};

	return updatedWidgetProps;
};

function mergeDataOptions(
	original: ChartWidgetProps['dataOptions'],
	aiResponse: Partial<ChartWidgetProps['dataOptions']>,
): ChartWidgetProps['dataOptions'] {
	const merged = { ...original };

	for (const key of Object.keys(aiResponse)) {
		const dataOptionsKey = key as keyof ChartWidgetProps['dataOptions'];
		const incomingList = aiResponse[dataOptionsKey];
		const originalList = original[dataOptionsKey] || [];

		// @ts-expect-error - TODO: fix this
		merged[dataOptionsKey] = originalList.map((originalItem: any) => {
			const originalName = originalItem?.column?.name;
			// @ts-expect-error - TODO: fix this
			const updatedItem = incomingList.find(
				(item: any) => item?.column?.name === originalName,
			);

			if (updatedItem) {
				const result = { ...originalItem }; // Preserve all original properties

				// Update or remove properties based on updatedItem
				Object.keys(updatedItem).forEach((key) => {
					if (key !== 'column') {
						const value = updatedItem[key];
						if (value === null || value === undefined) {
							delete result[key]; // Remove property if null/undefined in update
						} else {
							result[key] = value; // Update property with new value
						}
					}
				});

				return result;
			}

			return originalItem;
		});
	}

	return merged;
}

export const updateChartDataOptions = async (
	{ chartId, userPrompt }: z.infer<typeof updateChartSchema>,
	systemPrompt: string,
	context: BuildChartContext,
) => {
	const chartWidgetProps = context.retrieveChart?.(chartId);
	if (!chartWidgetProps) {
		throw new Error(`Chart not found`);
	}

	const updatedChartWidgetProps = await updateChartDataOptionsHelper({
		systemPrompt,
		userPrompt,
		chartWidgetProps,
	});
	context.saveChart?.(chartId, updatedChartWidgetProps);
	return {
		message: 'Chart data options updated successfully',
	};
};

/**
 * Configuration for data options update tools.
 */
type DataOptionsToolConfig = {
	toolName: string;
	buildSystemPrompt: (chartWidgetProps: ExtendedChartWidgetProps) => string;
};

/**
 * Factory function to create update chart data options tools.
 * Encapsulates common logic (chart retrieval, validation, calling updateChartDataOptions)
 * while allowing each tool to customize its system prompt generation.
 */
const createUpdateChartDataOptionsTool = (config: DataOptionsToolConfig) => {
	return async (
		{ chartId, userPrompt }: z.infer<typeof updateChartSchema>,
		context: BuildChartContext,
	) => {
		devDebug('toolCall', {
			tool: 'updateChartDataOptions',
			toolName: config.toolName,
			chartId,
			userPrompt,
		});
		const chartWidgetProps = context.retrieveChart?.(chartId);
		if (!chartWidgetProps) {
			throw new Error(`Chart not found`);
		}

		const systemPrompt = config.buildSystemPrompt(chartWidgetProps);
		return updateChartDataOptions(
			{ chartId, userPrompt },
			systemPrompt,
			context,
		);
	};
};

// Tool configurations
const updateChartTypeOfMeasuresConfig: DataOptionsToolConfig = {
	toolName: 'updateChartTypeOfMeasures',
	buildSystemPrompt: (chartWidgetProps) => {
		const baseSchema = getChartDataOptionsEditingSchemas(
			chartWidgetProps.chartType,
		);
		const filteredSchema = filterSchemaProperties(baseSchema, [
			'column',
			'chartType',
		]);
		return buildSystemPromptForDataOptions(
			chartWidgetProps,
			filteredSchema,
		);
	},
};

const updateChartSortingConfig: DataOptionsToolConfig = {
	toolName: 'updateChartSorting',
	buildSystemPrompt: (chartWidgetProps) => {
		const baseSchema = getChartDataOptionsEditingSchemas(
			chartWidgetProps.chartType,
		);
		const filteredSchema = filterSchemaProperties(baseSchema, [
			'column',
			'sortType',
		]);
		const additionalInstructions = `IMPORTANT: When setting sortType on a column within dataOptions, if sortType exists on any other column, set it to null to ensure only one is active.`;
		return buildSystemPromptForDataOptions(
			chartWidgetProps,
			filteredSchema,
			additionalInstructions,
		);
	},
};

const updateTrendAndForecastConfig: DataOptionsToolConfig = {
	toolName: 'updateTrendAndForecast',
	buildSystemPrompt: (chartWidgetProps) => {
		const baseSchema = getChartDataOptionsEditingSchemas(
			chartWidgetProps.chartType,
		);
		const filteredSchema = filterSchemaProperties(baseSchema, [
			'column',
			'trend',
			'forecast',
		]);
		return buildSystemPromptForDataOptions(
			chartWidgetProps,
			filteredSchema,
		);
	},
};

const updateOtherChartDataOptionsConfig: DataOptionsToolConfig = {
	toolName: 'updateOtherChartDataOptions',
	buildSystemPrompt: (chartWidgetProps) => {
		const baseSchema = getChartDataOptionsEditingSchemas(
			chartWidgetProps.chartType,
		);
		const filteredSchema = excludeSchemaProperties(baseSchema, [
			'sortType', // Has dedicated sorting tool
			'trend', // Has dedicated trend/forecast tool
			'forecast', // Has dedicated trend/forecast tool
			'chartType', // Has dedicated chartType tool
		]);
		const additionalInstructions = `IMPORTANT: This tool cannot swap data columns (e.g. changing "Revenue" to "Profit"). Use updateChartQuery for that.`;
		return buildSystemPromptForDataOptions(
			chartWidgetProps,
			filteredSchema,
			additionalInstructions,
		);
	},
};

// Generate and export wrapper functions
export const updateChartTypeOfMeasures = createUpdateChartDataOptionsTool(
	updateChartTypeOfMeasuresConfig,
);

export const updateChartSorting = createUpdateChartDataOptionsTool(
	updateChartSortingConfig,
);

export const updateTrendAndForecast = createUpdateChartDataOptionsTool(
	updateTrendAndForecastConfig,
);

export const updateOtherChartDataOptions = createUpdateChartDataOptionsTool(
	updateOtherChartDataOptionsConfig,
);
